import { AxiosResponse } from 'axios';
import {
  Comment,
  Feedback,
  ResponseApiUsersCheckins,
  ResponseApiUsersCheckinsSummary,
  UserCheckin,
  CheckinSummary,
} from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type GetCheckinAnswersProps = {
  checkinId: number | string;
  userId: string;
};

export type GetCheckinProps = {
  checkinId: string;
};

export type GetUserCheckinsSummaryProps = {
  userId: string;
};

export type GetCheckinFeedbacksProps = {
  checkinId: string;
};

export type PostCheckinFeedbacks = {
  checkinId: string;
  feedback: {
    text: string;
    userId: string;
  };
};

export type PostCommentApi = {
  checkinId: string;
  comment: {
    userId: string;
    answerId: string;
    text: string;
  };
};

export type UpdatePriorityStatus = {
  checkinId: string;
  answerId: string;
  isFinished?: boolean;
};

export type MarkAsReviewed = {
  checkinId: string;
  checkinReviewed: {
    isReviewed: boolean;
  };
};

export type PostCheckinApi = {
  id: string;
  answers: Array<{ description: string; answer: string | string[] }>;
};

export type GetCheckinsReport = {
  startDate: Date | string;
  endDate: Date | string;
  userIds?: string;
};

const getAllCheckins = async (): Promise<AxiosResponse<ResponseApiUsersCheckins>> => {
  const { data, error } = await supabase.rpc('rpc_all_users_checkins');
  if (error) throw error;
  return asResponse<ResponseApiUsersCheckins>({ usersCheckins: data || [] });
};

const getUsersCheckinsSummary = async (): Promise<AxiosResponse<ResponseApiUsersCheckinsSummary>> => {
  const { data, error } = await supabase.rpc('rpc_users_checkins_summary');
  if (error) throw error;
  return asResponse<ResponseApiUsersCheckinsSummary>({ summary: data || [] });
};

const getUserCheckinsByIdForAdmin = async (userId: string): Promise<AxiosResponse<CheckinSummary[]>> => {
  const { data, error } = await supabase
    .from('checkins')
    .select('*')
    .eq('profile_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse(data as unknown as CheckinSummary[]);
};

const getAllUsersCheckins = async (): Promise<AxiosResponse<CheckinSummary[]>> => {
  const { data, error } = await supabase
    .from('checkins')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse(data as unknown as CheckinSummary[]);
};

const getCheckinById = async ({ checkinId }: GetCheckinProps): Promise<AxiosResponse<UserCheckin>> => {
  const { data, error } = await supabase
    .from('checkins')
    .select('*, checkin_answers(*), checkin_feedbacks(*)')
    .eq('id', checkinId)
    .single();

  if (error) throw error;
  return asResponse(data as unknown as UserCheckin);
};

const getCheckinFeedbacks = async ({
  checkinId,
}: GetCheckinFeedbacksProps): Promise<AxiosResponse<Feedback[]>> => {
  const { data, error } = await supabase
    .from('checkin_feedbacks')
    .select('*')
    .eq('checkin_id', checkinId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return asResponse(data as unknown as Feedback[]);
};

const postCheckinFeedbacks = async ({
  checkinId,
  feedback,
}: PostCheckinFeedbacks): Promise<AxiosResponse<Feedback>> => {
  const { data, error } = await supabase
    .from('checkin_feedbacks')
    .insert({
      checkin_id: checkinId,
      author_id: feedback.userId,
      text: feedback.text,
    })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Feedback);
};

const postComment = async ({ checkinId, comment }: PostCommentApi): Promise<AxiosResponse<Comment>> => {
  const { data, error } = await supabase
    .from('comments')
    .insert({
      answer_id: comment.answerId,
      author_id: comment.userId,
      text: comment.text,
    })
    .select()
    .single();

  if (error) throw error;
  return asResponse(data as unknown as Comment);
};

const submitCheckin = async ({ id, answers }: PostCheckinApi): Promise<AxiosResponse> => {
  const rows = answers.map((a) => ({
    checkin_id: id,
    answer: typeof a.answer === 'string' ? a.answer : JSON.stringify(a.answer),
    question_text: a.description,
  }));

  const { data, error } = await supabase
    .from('checkin_answers')
    .insert(rows)
    .select();

  if (error) throw error;
  return asResponse(data);
};

const markAsReviewed = async ({ checkinId, checkinReviewed }: MarkAsReviewed): Promise<AxiosResponse> => {
  const { data, error } = await supabase
    .from('checkins')
    .update({ is_reviewed: checkinReviewed.isReviewed })
    .eq('id', checkinId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const updatePriorityStatus = async ({
  checkinId,
  answerId,
  isFinished,
}: UpdatePriorityStatus): Promise<AxiosResponse> => {
  const { data, error } = await supabase
    .from('checkin_answers')
    .update({ is_finished: isFinished })
    .eq('id', answerId)
    .eq('checkin_id', checkinId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const getAveragePulses = async ({
  userIds,
  startDate,
  endDate,
}: GetCheckinsReport): Promise<AxiosResponse> => {
  const { data, error } = await supabase.rpc('rpc_average_pulses', {
    p_start: new Date(startDate).toISOString(),
    p_end: new Date(endDate).toISOString(),
    p_user_ids: userIds || '',
  });
  if (error) throw error;
  return asResponse(data || { pulse: null });
};

const getCheckinsReport = async ({
  userIds,
  startDate,
  endDate,
}: GetCheckinsReport): Promise<AxiosResponse> => {
  const { data, error } = await supabase.rpc('rpc_checkins_report', {
    p_start: new Date(startDate).toISOString(),
    p_end: new Date(endDate).toISOString(),
    p_user_ids: userIds || '',
  });
  if (error) throw error;
  return asResponse(data || { submittedCheckIns: null });
};

export default {
  getUsersCheckinsSummary,
  getAllCheckins,
  // getUserCheckinsSummary,
  getCheckinById,
  getCheckinFeedbacks,
  postCheckinFeedbacks,
  postComment,
  markAsReviewed,
  getAveragePulses,
  getCheckinsReport,
  submitCheckin,
  getAllUsersCheckins,
  updatePriorityStatus,
  getUserCheckinsByIdForAdmin,
};
